// src\components\planning\SelectedBatchDetail.jsx
import React from 'react';
import { Clock, AlertTriangle, X, ChevronLeft } from 'lucide-react';

// Fonction pour obtenir la couleur de bordure selon le cycle
const getBorderColor = (cycleName) => {
  switch(cycleName) {
    case 'A42': return '#3b82f6'; // blue-500
    case 'A35': return '#8b5cf6'; // purple-500
    case 'E25': return '#22c55e'; // green-500
    case 'A29': return '#ef4444'; // red-500
    case 'E28': return '#eab308'; // yellow-500
    default: return '#6b7280'; // gray-500
  }
};

// Fonction pour obtenir la couleur de la barre de progression
const getProgressBarColor = (current, max) => {
  if (!max) return 'bg-gray-500';
  
  const percentage = (current / max) * 100;
  if (percentage < 50) return 'bg-green-500';
  if (percentage < 75) return 'bg-blue-500';
  if (percentage < 90) return 'bg-yellow-500';
  return 'bg-red-500';
};

const SelectedBatchDetail = ({ 
  batch, 
  resource, 
  assignedOrders, 
  onRemoveOrder,
  pendingOrders
}) => {
  if (!batch) return null;
  
  // Calculer les totaux de prises de vide et de thermocouples
  const totalVacuumPorts = assignedOrders.reduce((sum, order) => sum + order.vacuumPorts, 0);
  const totalTc = assignedOrders.reduce((sum, order) => sum + order.tc, 0);
  
  // Déterminer si les limites sont dépassées
  const vacuumPortsExceeded = resource && totalVacuumPorts > resource.maxVacuumPorts;
  const tcExceeded = resource && totalTc > resource.maxTc;
  
  // Fonction pour obtenir la couleur du cycle
  const getCycleColor = (cycleName) => {
    switch(cycleName) {
      case 'A42': return 'border-blue-500 bg-blue-50';
      case 'A35': return 'border-purple-500 bg-purple-50';
      case 'E25': return 'border-green-500 bg-green-50';
      case 'A29': return 'border-red-500 bg-red-50';
      case 'E28': return 'border-yellow-500 bg-yellow-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };
  
  // Vérifier si la date de l'OF est après la date de la fournée
  const isOrderLate = (order) => {
    // Convertir les dates de format "DD/MM/YYYY" à des objets Date
    const [batchDay, batchMonth, batchYear] = batch.date.split('/').map(Number);
    const [orderDay, orderMonth, orderYear] = order.deadline.split('/').map(Number);
    
    const batchDate = new Date(batchYear, batchMonth - 1, batchDay);
    const orderDate = new Date(orderYear, orderMonth - 1, orderDay);
    
    return batchDate > orderDate;
  };

  return (
    <div className={`border-t border-b mb-4 ${getCycleColor(batch.cycleName)}`}>
      <div className="p-3 border-l-4 border-b flex justify-between items-center" style={{ borderLeftColor: getBorderColor(batch.cycleName) }}>
        <div>
          <h2 className="font-bold text-lg">{batch.batchNumber}</h2>
          <div className="flex items-center text-sm space-x-4">
            <div className="font-medium">{batch.cycleName} - {batch.code}</div>
            <div className="text-gray-600">
              <span className="inline-flex items-center">
                <Clock size={14} className="mr-1" />
                {batch.startTime} - {batch.endTime}
              </span>
            </div>
          </div>
        </div>
        <div className="text-sm font-medium">{resource?.name || 'Moyen inconnu'}</div>
      </div>
      
      {/* Totaux et alertes */}
      <div className="p-3 bg-white border-b">
        <div className="flex justify-between items-center">
          <div className="font-medium">OF assignés: {assignedOrders.length}</div>
          <div className="flex space-x-4">
            <div className={`flex items-center ${vacuumPortsExceeded ? 'text-red-600 font-bold' : ''}`}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" className="mr-1 text-purple-600">
                <circle cx="12" cy="12" r="10" fill="none" strokeWidth="2"></circle>
                <path d="M8 12h8" strokeWidth="2"></path>
                <path d="M12 8v8" strokeWidth="2"></path>
              </svg>
              <span>
                {totalVacuumPorts}/{resource?.maxVacuumPorts || '?'} vides
                {vacuumPortsExceeded && ' (Dépassé!)'}
              </span>
            </div>
            <div className={`flex items-center ${tcExceeded ? 'text-red-600 font-bold' : ''}`}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" className="mr-1 text-red-600">
                <path d="M12 2v6M12 22v-6M4.93 4.93l4.24 4.24M14.83 14.83l4.24 4.24M2 12h6M22 12h-6M4.93 19.07l4.24-4.24M14.83 9.17l4.24-4.24" fill="none" strokeWidth="2"></path>
              </svg>
              <span>
                {totalTc}/{resource?.maxTc || '?'} TC
                {tcExceeded && ' (Dépassé!)'}
              </span>
            </div>
          </div>
        </div>
        
        {/* Barre de progression pour le remplissage */}
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
              <div 
                className={`h-2.5 rounded-full ${getProgressBarColor(totalVacuumPorts, resource?.maxVacuumPorts)}`} 
                style={{ width: `${Math.min((totalVacuumPorts / (resource?.maxVacuumPorts || 1)) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
          <div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
              <div 
                className={`h-2.5 rounded-full ${getProgressBarColor(totalTc, resource?.maxTc)}`} 
                style={{ width: `${Math.min((totalTc / (resource?.maxTc || 1)) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Message d'aide pour le glisser-déposer */}
      {assignedOrders.length === 0 && (
        <div className="px-3 py-4 bg-blue-100 text-blue-800 border-b text-center text-sm">
          <div className="flex items-center justify-center mb-2">
            <ChevronLeft size={20} className="animate-pulse mr-2" />
            <span>Glissez les OF depuis la liste de droite</span>
          </div>
          <p>Vous pouvez aussi cliquer sur le bouton "Assigner" des OF compatibles</p>
        </div>
      )}
      
      {/* Liste des OF assignés */}
      <div className="p-3 max-h-[59vh] overflow-y-auto bg-white">
        {assignedOrders.length > 0 ? (
          <div className="space-y-2">
            {assignedOrders.map(order => (
              <div 
                key={order.id}
                className="p-2 border rounded-md flex justify-between items-start hover:bg-gray-50 transition-colors"
              >
                <div>
                  <div className="flex items-center">
                    <div className="font-medium">{order.id}</div>
                    {isOrderLate(order) && (
                      <div className="ml-2 flex items-center text-red-600 text-xs">
                        <AlertTriangle size={14} className="mr-1" />
                        OF en retard !
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-gray-600">{order.name}</div>
                  <div className="flex items-center space-x-4 mt-1 text-xs">
                    <div className="text-gray-500">Échéance: {order.deadline}</div>
                    <div className="flex items-center">
                      <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" className="mr-1 text-purple-600">
                        <circle cx="12" cy="12" r="10" fill="none" strokeWidth="2"></circle>
                        <path d="M8 12h8" strokeWidth="2"></path>
                        <path d="M12 8v8" strokeWidth="2"></path>
                      </svg>
                      <span>{order.vacuumPorts}</span>
                    </div>
                    <div className="flex items-center">
                      <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" className="mr-1 text-red-600">
                        <path d="M12 2v6M12 22v-6M4.93 4.93l4.24 4.24M14.83 14.83l4.24 4.24M2 12h6M22 12h-6M4.93 19.07l4.24-4.24M14.83 9.17l4.24-4.24" fill="none" strokeWidth="2"></path>
                      </svg>
                      <span>{order.tc}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-start">
                  <button 
                    className="text-gray-400 hover:text-red-600 p-1 transition-colors"
                    onClick={() => onRemoveOrder(order.id)}
                    aria-label="Retirer l'OF"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-400">
            Aucun OF assigné à cette fournée
          </div>
        )}
      </div>
      
      {/* Conseils pour l'interface tactile */}
      {assignedOrders.length > 0 && (
        <div className="p-2 text-xs text-center text-gray-500 border-t bg-gray-50 touch-hint">
          <p>Appuyez sur X pour retirer un OF de la fournée</p>
        </div>
      )}
    </div>
  );
};

export default SelectedBatchDetail;